// This is a standalone uploader that can be used to
// upload a file.  It is view-agnostic, so it
// can be used independently of the file picking UI.
//
// Errors: (these can be translated to user readable copy)
//   size-too-large - File size is too large
//   invalid-type - File type is unsupported
//   file-name-too-long - The file name is too long
//

import _ from 'lodash';
import $r from 'sitewide-js/core_pre_webpack/routes';
import $ from 'sitewide-jquery';
import Kale from 'js/core_webpack/metrics/kale';

export const DEFAULT_ACCEPTED_FILE_EXTENSIONS = [
  'pdf',
  'txt',
  'ps',
  'rtf',
  'epub',
  'odt',
  'odp',
  'ods',
  'odg',
  'odf',
  'sxw',
  'sxc',
  'sxi',
  'sxd',
  'doc',
  'ppt',
  'pps',
  'xls',
  'docx',
  'pptx',
  'ppsx',
  'xlsx',
  'tif',
  'tiff',
  'csv',
  'tsv',
];
interface Options {
  maxSize: number;
  maxFileNameLength: number;
  upload_to_s3: boolean;
  s3UploadUrl?: string;
  microsite_asset_filename?: string;
  fileTypes: string[];
  url?: string;
  source?: string;
  note?: string;
  ownerId?: number;
  extraParams?: Record<string, unknown>;
}

export interface Attachment {
  id: number;
  title: string;
  file_type: string;
  scribd_thumbnail_url: string | null;
  file_name: string;
  download_url: string;
}

export interface UploadResult {
  attachment: Attachment;
}

export type UploadPromise = JQuery.Promise<UploadResult, Error, number>;

type UploadDeferred = JQuery.Deferred<UploadResult, Error, number>;

type Error =
  | { error?: string; message?: string }
  | {
      jqXHR: JQueryXHR;
      textStatus: JQuery.Ajax.ErrorTextStatus;
      errorThrown?: string;
      error: undefined;
    };
export default class FileUploader {
  options: Options;
  constructor(options: Partial<Options>) {
    if (!options) {
      options = {};
    }
    this.options = _.extend({}, this.defaultOptions(), options) as Options;
  }

  /** @param {File} file*/
  upload(file: File): UploadPromise {
    const dfd: UploadDeferred = $.Deferred();
    const validationError = this.validateFile(file);

    const upload_start_time = Date.now();

    if (validationError) {
      _.defer(() =>
        dfd.reject({
          // TODO: Use `message` instead of `error` everywhere, then remove `error` here.
          error: validationError,
          message: this.getErrorMessage(validationError),
        }),
      );
    } else {
      if (this.options.upload_to_s3) {
        this.uploadFileToS3(file, dfd);
      } else {
        this.uploadFile(file, dfd);
      }
    }

    dfd.then((_: unknown) => {
      this.recordStats(Date.now() - upload_start_time, file.size, 'entire');
    });

    return dfd.promise();
  }

  // PRIVATE:

  uploadFile(file: File, dfd: UploadDeferred) {
    const fd = new FormData();
    fd.append('files[]', file);

    const request = $.ajax({
      url: this.url(),
      data: fd,
      processData: false,
      contentType: false,
      type: 'POST',

      // Use custom XHR for progress
      xhr() {
        const xhr = new window.XMLHttpRequest();

        xhr.upload.addEventListener(
          'progress',
          function (e) {
            if (e.lengthComputable) {
              return dfd.notify(e.loaded / e.total);
            }
          },
          false,
        );

        return xhr;
      },
    });

    // Proxy upload results to deferred
    request.done(dfd.resolve);
    request.fail(dfd.reject);
  }

  uploadFileToS3(file: File, dfd: UploadDeferred) {
    const promiseGen = async (): Promise<UploadResult> => {
      const get_s3_upload_info = Date.now();

      /** @type {{upload_url: string, uuid: string, content_type?: string}}*/
      const s3_upload_info = await $.ajax({
        url: this.apiUrl(),
        data: {
          fileLength: file.size,
          filename: this.options.microsite_asset_filename,
        },
      });

      this.recordStats(Date.now() - get_s3_upload_info, file.size, 'info');

      const to_s3_start = Date.now();

      let result = await $.ajax({
        url: s3_upload_info.upload_url,
        // Microsite asset URLs are signed for a specific Content-Type
        contentType: s3_upload_info.content_type ?? file.type,
        data: file,
        processData: false,
        type: 'PUT',

        // Use custom XHR for progress
        xhr() {
          const xhr = new window.XMLHttpRequest();

          // prevent errors from trying to parse an empty string as xml
          xhr.overrideMimeType('text/plain; charset=x-user-defined');

          xhr.upload.addEventListener(
            'progress',
            function (e) {
              if (e.lengthComputable) {
                return dfd.notify(e.loaded / (e.total * 1.01));
              }
            },
            false,
          );

          return xhr;
        },
      });

      this.recordStats(Date.now() - to_s3_start, file.size, 'to_s3');

      if (!this.options.microsite_asset_filename) {
        const create_attachment_start = Date.now();
        result = await $.ajax({
          url: this.url(),
          type: 'POST',
          dataType: 'json',
          data: {
            uuid: s3_upload_info.uuid,
            filename: file.name,
            owner_id: this.options.ownerId,
            note: this.options.note,
            ...this.options.extraParams,
          },
        });

        this.recordStats(
          Date.now() - create_attachment_start,
          file.size,
          'create_attachment',
        );
      }

      return result;
    };

    const promise = promiseGen();

    promise.then(dfd.resolve);
    promise.catch(dfd.reject);
  }

  recordStats(time: number, file_size: number, upload_process_part: string) {
    Kale.notify({
      collectionName: 'sutd_regpath_july_2018',
      eventData: {
        event: `file_upload`,
        step_name: upload_process_part,
        mention_category: this.options.upload_to_s3 ? 's3' : 'traditional',
        mention_count: time,
        related_work_count: file_size,
      },
    });
  }

  defaultOptions(): Partial<Options> {
    return {
      maxSize: 104857600, // 100MB
      maxFileNameLength: 125,
      upload_to_s3: false,
      fileTypes: DEFAULT_ACCEPTED_FILE_EXTENSIONS,
    };
  }

  url() {
    if (this.options.url != null) {
      return this.options.url;
    } else {
      return this.attachmentUrl();
    }
  }

  apiUrl() {
    if (this.options.microsite_asset_filename) {
      return $r.microsite_asset_upload_url_api_v0_attachments_path({
        subdomain_param: 'api',
      });
    } else {
      return (
        this.options.s3UploadUrl ??
        $r.upload_url_api_v0_attachments_path({ subdomain_param: 'api' })
      );
    }
  }

  attachmentUrl() {
    return $r.api_v0_attachments_path({
      source: this.options.source,
      subdomain_param: 'api',
    });
  }

  validateFile(file: File) {
    return (
      this.validateSize(file) ||
      this.validateType(file) ||
      this.validateName(file)
    );
  }

  validateSize(file: File) {
    if (file.size > this.options.maxSize) {
      return 'size-too-large';
    }
  }

  validateType(file: File) {
    const extension = _.last(file.name.split(/\./))?.toLowerCase();
    if (!extension || !_.includes(this.options.fileTypes, extension)) {
      return 'invalid-type';
    }
  }

  validateName(file: File) {
    if (
      !file.name ||
      file.name.length > (this.options.maxFileNameLength ?? 125)
    ) {
      return 'file-name-too-long';
    }
  }

  getErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'file-name-too-long':
        return `File name exceeds the maximum length of ${this.options.maxFileNameLength} characters.`;
      case 'size-too-large': {
        const maxSizeMB = Math.round(this.options.maxSize / (1024 * 1024));
        return `File size exceeds the maximum limit of ${maxSizeMB}MB.`;
      }
      case 'invalid-type':
        return `File type is not supported. Supported types: ${this.options.fileTypes.join(', ')}.`;
      default:
        return 'An error occurred while uploading the file.';
    }
  }

  static documentTitleFromFilename(filename: string) {
    // remove extension(s)
    filename = filename.replace(/(\.[a-z]+)*$/g, '');

    filename = this._splitUpFilenameWords(filename);

    // squeeze any runs of more than one space in a row
    filename = filename.replace(/\s+/g, ' ');

    // strip leading & trailing runs of numbers & spaces
    filename = filename.replace(/^[0-9 ]+/, '');
    filename = filename.replace(/[0-9 ]+$/, '');

    // capitalize the first letter
    filename = filename.charAt(0).toUpperCase() + filename.slice(1);

    return filename;
  }

  static _splitUpFilenameWords(filename: string) {
    // underscores are our least-ambiguous possible separator, replace them first
    filename = filename.replace(/_/g, ' ');

    // replace dashes, if there are multiple
    if (filename.match(/-.*-/)) {
      filename = filename.replace(/-/g, ' ');
    }

    // split up camel case only if there are still no spaces
    if (filename.indexOf(' ') < 0) {
      filename = filename.replace(/([a-z])([A-Z])/g, '$1 $2');
    }

    return filename;
  }
}
